// @ts-nocheck
import { Component, Input, Output, EventEmitter, ChangeDetectionStrategy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { handleFooterQuickLinkClick } from '@way/theme-host/shared/utils/footer-quick-links.util';

export interface FooterLink {
  label: string;
  link: string;
}

export interface SocialLink {
  platform: string;
  url: string;
  icon: string;
}

/**
 * PageFooterComponent renders a configurable footer section for a minimal website template including logo, quick links, social links and visual theming options.
 * @component
 * @selector app-page-footer
 * @example
 *   <app-page-footer
 *     [logo]="{ text: 'Way', image: '/assets/logo.png' }"
 *     [quickLinks]="quickLinksArray"
 *     [quickLinksTitle]="'Quick links'"
 *     [socialLinks]="socialLinksArray"
 *     [socialLinksTitle]="'Connect with us'"
 *     [copyright]="`Copyright © ${new Date().getFullYear()} way.com. All rights reserved.`"
 *     [backgroundColor]="'#fff'"
 *     [textColor]="'#222'"
 *     [linkColor]="'#007bff'"
 *     [separatorColor]="'#eaeaea'">
 *   </app-page-footer>
 * @input {object=} logo - Optional logo object displayed in the footer. Shape: { text?: string, image?: string } where `text` is the alt/title text and `image` is the logo URL.
 * @input {FooterLink[]} quickLinks - Array of quick link objects shown in the footer. Default includes Home, Express Washes, Coupons, Memberships, Express Details, Fleets. Each FooterLink has at least { label: string, link: string }.
 * @input {string} quickLinksTitle - Title for the quick links section. Default: 'Quick links'.
 * @input {SocialLink[]} socialLinks - Array of social link objects for social icons. Default includes Instagram, Facebook, YouTube, LinkedIn, X. Each SocialLink has at least { platform: string, url: string, icon: string }.
 * @input {string} socialLinksTitle - Title for the social links section. Default: 'Connect with us'.
 * @input {string} copyright - Copyright text shown in the footer. Default: `Copyright © ${new Date().getFullYear()} way.com. All rights reserved.`.
 * @input {string=} backgroundColor - Optional CSS color for the footer background. If omitted, component falls back to stylesheet defaults.
 * @input {string=} textColor - Optional CSS color for footer text (maps to footer.theme.text_color when used with theming).
 * @input {string=} linkColor - Optional CSS color for footer links (maps to footer.theme.link_color).
 * @input {string=} separatorColor - Optional CSS color for separators/dividers in the footer (maps to footer.theme.separator_color).
 * @output none - This component is presentational and does not emit events; interactions are handled via links and external navigation.
 *
 * Key behavior:
 * - Purely presentational component intended to display a footer with configurable content and colors.
 * - Accepts arrays of link objects to render lists and social icons.
 *
 * Lifecycle and methods:
 * - No custom lifecycle hooks (OnInit, OnDestroy, etc.) or instance methods are implemented in this component by default.
 * - Consumers may control content via inputs; styling and responsive behavior are provided by the component template and stylesheet.
 */
@Component({
  selector: 'app-page-footer',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './footer.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrls: ['./footer.component.scss'],
})
export class PageFooterComponent {
  @Input() logo?: {
    text?: string;
    image?: string;
  };

  @Input() quickLinks: FooterLink[] = [
    { label: 'Home', link: '#home' },
    { label: 'Express Washes', link: '#express-wash-packages' },
    { label: 'Coupons', link: '#car-wash-coupons--way' },
    { label: 'Memberships', link: '#memberships' },
    { label: 'Express Details', link: '#express-auto-details' },
    { label: 'Fleets', link: '#fleet-management' },
  ];

  @Input() quickLinksTitle = 'Quick links';

  @Input() socialLinks: SocialLink[] = [
    { platform: 'Instagram', url: '#', icon: 'instagram' },
    { platform: 'Facebook', url: '#', icon: 'facebook' },
    { platform: 'YouTube', url: '#', icon: 'youtube' },
    { platform: 'LinkedIn', url: '#', icon: 'linkedin' },
    { platform: 'X', url: '#', icon: 'x' },
  ];

  @Input() socialLinksTitle = 'Connect with us';

  @Input() copyright = `Copyright © ${new Date().getFullYear()} way.com. All rights reserved.`;

  @Input() backgroundColor?: string;
  @Input() textColor?: string; // From footer.theme.text_color
  @Input() linkColor?: string; // From footer.theme.link_color
  @Input() separatorColor?: string; // From footer.theme.separator_color
  @Input() deviceType: 'desktop' | 'tablet' | 'mobile' = 'desktop';
  @Output() quickLinkClick = new EventEmitter<string>();

  onQuickLinkClick(event: Event, link: FooterLink): void {
    handleFooterQuickLinkClick(event, link, pageType => this.quickLinkClick.emit(pageType));
  }

  trackByIndex(index: number): number {
    return index;
  }
  trackByValue(_: number, item: any): any {
    return item?.value ?? item;
  }
  trackById(_: number, item: any): any {
    return item?.id ?? item;
  }
  trackByString(_: number, s: any): any {
    return s;
  }
}
